import React, { useState } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Table,
  Pagination,
  Spinner,
} from 'react-bootstrap';
import { FaSearch, FaFileExcel, FaExclamationCircle } from 'react-icons/fa';
import { default as Selector } from 'react-select';
import Header from '~/Components/Header/Header';
import * as api from '~/Services/Service';
import { toast } from 'react-toastify';
import { salvarEmExcel } from '~/Utils/Helpers';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';

const OPCOES_ORDEM = [
  { value: 'CODIGO', label: 'Código de Cidade' },
  { value: 'NOME', label: 'Nome de Cidade' },
  { value: 'ESTADO_NOME', label: 'Estado + Nome de Cidade' },
  { value: 'REGIAO_NOME', label: 'Região + Nome de Cidade' },
];

const OPCOES_ESTADO = [
  { value: 'AC', label: 'AC - Acre' },
  { value: 'AL', label: 'AL - Alagoas' },
  { value: 'AP', label: 'AP - Amapá' },
  { value: 'AM', label: 'AM - Amazonas' },
  { value: 'BA', label: 'BA - Bahia' },
  { value: 'CE', label: 'CE - Ceará' },
  { value: 'DF', label: 'DF - Distrito Federal' },
  { value: 'ES', label: 'ES - Espírito Santo' },
  { value: 'GO', label: 'GO - Goiás' },
  { value: 'MA', label: 'MA - Maranhão' },
  { value: 'MT', label: 'MT - Mato Grosso' },
  { value: 'MS', label: 'MS - Mato Grosso do Sul' },
  { value: 'MG', label: 'MG - Minas Gerais' },
  { value: 'PA', label: 'PA - Pará' },
  { value: 'PB', label: 'PB - Paraíba' },
  { value: 'PR', label: 'PR - Paraná' },
  { value: 'PE', label: 'PE - Pernambuco' },
  { value: 'PI', label: 'PI - Piauí' },
  { value: 'RJ', label: 'RJ - Rio de Janeiro' },
  { value: 'RN', label: 'RN - Rio Grande do Norte' },
  { value: 'RS', label: 'RS - Rio Grande do Sul' },
  { value: 'RO', label: 'RO - Rondônia' },
  { value: 'RR', label: 'RR - Roraima' },
  { value: 'SC', label: 'SC - Santa Catarina' },
  { value: 'SP', label: 'SP - São Paulo' },
  { value: 'SE', label: 'SE - Sergipe' },
  { value: 'TO', label: 'TO - Tocantins' },
];

const RECORDS_PER_PAGE = 15;

const RelatorioCidades = () => {
  const [cidades, setCidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportandoExcel, setExportandoExcel] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filtrosAtivos, setFiltrosAtivos] = useState(null);

  const [ordem, setOrdem] = useState(OPCOES_ORDEM[0]);
  const [estado, setEstado] = useState(null);
  const [busca, setBusca] = useState('');

  const totalPages = Math.ceil(cidades.length / RECORDS_PER_PAGE);

  const sortedCidades = React.useMemo(() => {
    if (!sortConfig.key) return cidades;
    return [...cidades].sort((a, b) => {
      const aVal = a[sortConfig.key] != null ? a[sortConfig.key] : '';
      const bVal = b[sortConfig.key] != null ? b[sortConfig.key] : '';
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [cidades, sortConfig]);

  const currentRecords = React.useMemo(() => {
    const last = currentPage * RECORDS_PER_PAGE;
    const first = last - RECORDS_PER_PAGE;
    return sortedCidades.slice(first, last);
  }, [sortedCidades, currentPage]);

  const handleSort = col => {
    setSortConfig(prev => ({
      key: col,
      direction: prev.key === col && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleBuscar = async () => {
    const filtros = {
      ordem: ordem ? ordem.value : 'CODIGO',
      estado: estado && estado.value ? estado.value : null,
      busca: busca.trim() ? busca.trim() : null,
    };
    setFiltrosAtivos(filtros);
    try {
      setLoading(true);
      const resultado = await api.consultarRelatorioCidades(filtros);
      setCidades(resultado);
      setCurrentPage(1);
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao buscar cidades');
    } finally {
      setLoading(false);
    }
  };

  const handleExportarExcel = async () => {
    try {
      setExportandoExcel(true);
      const filtros = filtrosAtivos != null ? filtrosAtivos : {};
      await salvarEmExcel(filtros, '/relatorios/cidades/exportar-excel');
      toast.success('Relatório exportado com sucesso!');
    } catch (err) {
      toast.error('Erro ao exportar relatório');
    } finally {
      setExportandoExcel(false);
    }
  };

  const renderPagination = () => {
    const items = [];
    const maxVisible = 5;
    const start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);

    if (start > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => setCurrentPage(1)}>
          1
        </Pagination.Item>
      );
      if (start > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
      }
    }

    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === currentPage}
          onClick={() => setCurrentPage(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  const sortIcon = col => {
    if (sortConfig.key !== col) return ' ↕';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const renderBarraPaginacao = () => {
    if (totalPages <= 1) return null;
    return (
      <Row className="justify-content-end">
        <Col xs="auto">
          <Pagination className="d-inline-flex mb-0">
            <Pagination.First
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {renderPagination()}
            <Pagination.Next
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </Col>
      </Row>
    );
  };

  return (
    <div>
      <Header title="Relatório de Cidades" />

      <div className="container-fluid bg-white p-3">
        <Row>
          <Col xs={12} sm={6} md={3}>
            <Form.Group>
              <Form.Label>Ordenação</Form.Label>
              <Selector
                options={OPCOES_ORDEM}
                value={ordem}
                onChange={setOrdem}
                styles={customStyles}
                components={{ DropdownIndicator }}
                placeholder="Selecione..."
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={2}>
            <Form.Group>
              <Form.Label>Estado</Form.Label>
              <Selector
                options={OPCOES_ESTADO}
                value={estado}
                onChange={setEstado}
                styles={customStyles}
                components={{ DropdownIndicator }}
                placeholder="Todos..."
                isClearable
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Form.Group>
              <Form.Label>Busca</Form.Label>
              <Form.Control
                type="text"
                placeholder="Buscar por nome da cidade..."
                value={busca}
                onChange={e => setBusca(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={2}>
            <Form.Group>
              <Form.Label>&nbsp;</Form.Label>
              <Button
                type="button"
                variant="primary"
                className="w-100"
                onClick={handleBuscar}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="mr-2"
                    />
                    Buscando...
                  </>
                ) : (
                  <>
                    <FaSearch className="mr-2" />
                    Buscar
                  </>
                )}
              </Button>
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={2}>
            <Form.Group>
              <Form.Label>&nbsp;</Form.Label>
              <Button
                type="button"
                variant="success"
                className="w-100"
                onClick={handleExportarExcel}
                disabled={exportandoExcel || cidades.length === 0}
              >
                {exportandoExcel ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="mr-2"
                    />
                    Exportando...
                  </>
                ) : (
                  <>
                    <FaFileExcel className="mr-2" />
                    Exportar Excel
                  </>
                )}
              </Button>
            </Form.Group>
          </Col>
        </Row>
      </div>

      {cidades.length > 0 ? (
        <>
          <div className="container-fluid bg-white p-3 mt-3">
            {renderBarraPaginacao()}
          </div>
          <div className="container-fluid bg-white table-responsive">
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort('codigo_cidade')}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Código{sortIcon('codigo_cidade')}
                  </th>
                  <th
                    onClick={() => handleSort('descricao_cidade')}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Descrição{sortIcon('descricao_cidade')}
                  </th>
                  <th
                    onClick={() => handleSort('estado')}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Estado{sortIcon('estado')}
                  </th>
                  <th
                    onClick={() => handleSort('ddd')}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    DDD{sortIcon('ddd')}
                  </th>
                  <th
                    onClick={() => handleSort('descricao_regiao')}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Região{sortIcon('descricao_regiao')}
                  </th>
                  <th
                    onClick={() => handleSort('cep_inicial')}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    CEP Ini.{sortIcon('cep_inicial')}
                  </th>
                  <th
                    onClick={() => handleSort('cep_final')}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    CEP Fin.{sortIcon('cep_final')}
                  </th>
                  <th
                    onClick={() => handleSort('latitude')}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Latitude{sortIcon('latitude')}
                  </th>
                  <th
                    onClick={() => handleSort('longitude')}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Longitude{sortIcon('longitude')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentRecords.map(item => (
                  <tr key={`cidade-${item.codigo_cidade}`}>
                    <td>{item.codigo_cidade}</td>
                    <td>{item.descricao_cidade}</td>
                    <td>{item.estado}</td>
                    <td>{item.ddd}</td>
                    <td>{item.descricao_regiao}</td>
                    <td>{item.cep_inicial}</td>
                    <td>{item.cep_final}</td>
                    <td>{item.latitude}</td>
                    <td>{item.longitude}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="container-fluid bg-white p-3">
            {renderBarraPaginacao()}
          </div>
        </>
      ) : (
        !loading &&
        filtrosAtivos != null && (
          <div className="container-fluid bg-white p-5 mt-3 text-center">
            <Row>
              <Col>
                <FaExclamationCircle size={48} className="text-muted mb-3" />
                <h5 className="text-muted">Nenhum registro encontrado</h5>
                <p className="text-muted">Ajuste os filtros e tente novamente</p>
              </Col>
            </Row>
          </div>
        )
      )}
    </div>
  );
};

export default RelatorioCidades;
